import { Select } from 'antd';
import React from 'react';

import Field from '@fmbt/ui/Field';
import { LoadingSpinnerDiv } from '@fmbt/ui/LoadingSpinner';
import ContactSummary from 'components/composites/ContactSummary';
import DefaultFormItem from 'components/composites/DefaultFormItem';
import InlineHelp from 'components/composites/InlineHelp';
import FieldValue from 'components/forms/FieldValue';
import FormItemCol from 'components/forms/FormItemCol';
import Enterprise, { EnterpriseId } from 'types/models/enterprise';
import Site from 'types/models/site';
import User from 'types/models/user';

import { useAdminUsers } from './useAdminUsers';

const PRIMARY_CONTACT_TOOLTIP_LINK =
  'https://intercom.help/farmbot-helpcentre/en/articles/5486656-what-is-the-difference-between-an-admin-supervisor-and-view-only-role';

interface Props {
  enterpriseId: EnterpriseId | undefined
  readOnly?: boolean;
  adminTitle: string;
  initialOwner: Enterprise['owner'] | Site['owner'] | undefined;
  /** Use responsive column width */
  isFullSpanOnMobileHalfOnDesktop?: boolean;
  skipValidation?: boolean;
  emptyUsersComponent?: React.ReactNode;
}

export default function AdminUsersDropdown({
  enterpriseId,
  readOnly,
  adminTitle,
  initialOwner,
  isFullSpanOnMobileHalfOnDesktop,
  skipValidation,
  emptyUsersComponent
}: Props): JSX.Element | null {
  const { adminUsers: enterpriseAdmins, isLoading } = useAdminUsers(enterpriseId);

  if (isLoading) {
    return (
      <LoadingSpinnerDiv height={100} />
    );
  }

  if (
    !isLoading &&
    enterpriseAdmins.length === 0 &&
    emptyUsersComponent
  ) {
    return <>{emptyUsersComponent}</>;
  }

  const ownerOptions = enterpriseAdmins.map(({ firstName, lastName, id }) => ({
    label: `${firstName} ${lastName}`,
    value: id
  }));

  return (
    <>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <DefaultFormItem
          // View-only users cannot select or change the owner, so they do not need to see the tooltip.
          label={readOnly ? adminTitle : (
            <>
              {adminTitle}
              <InlineHelp
                textContent={`${adminTitle} must be an Admin user.`}
                helpCentreHref={PRIMARY_CONTACT_TOOLTIP_LINK}
              />
            </>
          )}
          name="ownerId"
          initialValue={initialOwner?.id || ownerOptions[0]?.value}
          rules={[
            {
              required: !skipValidation,
              message: 'Please select an Owner'
            }
          ]}
          readOnly={readOnly}
          formatReadOnlyValue={(ownerId) => ownerOptions.find(({ value }) => value === ownerId)?.label
            ?? ownerId
            ?? 'None'
          }
        >
          <Select options={ownerOptions} />
        </DefaultFormItem>
      </FormItemCol>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <FieldValue name="ownerId">
          {(ownerId) => {
            const user: User | undefined =
              ownerId !== initialOwner?.id
                ? enterpriseAdmins.find((item) => item.id === ownerId)
                : initialOwner ?? undefined;
            if (!user) return null;
            return (
              // mb-24 to match the margin-bottom of DefaultFormItem
              <div className="mb-24">
                <Field label="Contact Details">
                  <ContactSummary contact={user} />
                </Field>
              </div>
            );
          }}
        </FieldValue>
      </FormItemCol>
    </>
  );
}
